import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { terbilangRupiah } from '@/lib/terbilang';
import { useSession } from '@/lib/SessionContext';

import { getCompanyAbbr, getSupplierAbbr } from '@/lib/poNumberGenerator';

const fmt = (n) => n != null ? new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) : '0,00';

const fmtQtyVal = (n) => {
  if (n == null) return '0';
  const num = Number(n);
  if (isNaN(num)) return '0';
  if (Number.isInteger(num)) {
    return new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(num);
  }
  return new Intl.NumberFormat('id-ID', { maximumFractionDigits: 2 }).format(num);
};

export default function POPrint({ data }) {
  if (!data) return null;

  const { member: sessionMember, companySettings: sessionCompanySettings } = useSession() || {};

  const { data: dbSuppliers = [] } = useQuery({
    queryKey: ['supplier-database-poprint'],
    queryFn: async () => {
      try {
        const res = await base44.entities.SupplierDatabase?.list();
        return res || [];
      } catch (err) {
        return [];
      }
    },
    staleTime: 5 * 60 * 1000
  });

  const { data: dbTeamMembers = [] } = useQuery({
    queryKey: ['team-member-poprint'],
    queryFn: async () => {
      try {
        const res = await base44.entities.TeamMember?.list();
        return res || [];
      } catch (err) {
        return [];
      }
    },
    staleTime: 5 * 60 * 1000
  });

  const {
    po_number,
    created_date,
    nama_sales,
    metode_pembayaran,
    tanggal_jatuh_tempo,
    bank_tujuan,
    nomor_rekening,
    atas_nama,
    pic,
    batch_items,
    ongkir,
    ongkir_mode,
    catatan,
    kurs_usd,
    kurs_eur,
    companySettings,
    buyer_info,
    supplier_info
  } = data;

  const dateObj = created_date ? new Date(created_date) : new Date();
  const validDate = dateObj && !isNaN(dateObj.getTime()) ? dateObj : new Date();
  const dateStr = validDate.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });

  let items = [];
  try {
    items = typeof batch_items === 'string' ? JSON.parse(batch_items) : (batch_items || []);
  } catch (e) {
    items = [];
  }

  // Fallback for single item purchases (non-batch)
  if (items.length === 0 && data.nama_item) {
    items = [{
      name: data.nama_item,
      qty: data.qty_beli,
      satuan: data.satuan || '',
      harga_rupiah: data.harga_unit,
      mata_uang: data.mata_uang || (data.harga_dollar || data.kurs_usd ? 'USD' : (data.harga_euro || data.kurs_eur ? 'EUR' : 'IDR')),
      harga_dollar: data.harga_dollar != null ? data.harga_dollar : (data.kurs_usd ? data.harga_unit / data.kurs_usd : null),
      harga_euro: data.harga_euro != null ? data.harga_euro : (data.kurs_eur ? data.harga_unit / data.kurs_eur : null),
      ppn_type: data.ppn_nominal > 0 ? 'exclude' : 'include',
      ppn_percentage: data.ppn_percentage || 11,
      spesifikasi: data.spesifikasi || data.kode_item || ''
    }];
  }

  // Parse buyer info
  let buyerData = null;
  if (buyer_info) {
    try {
      buyerData = typeof buyer_info === 'string' ? JSON.parse(buyer_info) : buyer_info;
    } catch (e) {
      buyerData = null;
    }
  }

  // Parse supplier info
  let supplierData = null;
  if (supplier_info) {
    try {
      supplierData = typeof supplier_info === 'string' ? JSON.parse(supplier_info) : supplier_info;
    } catch (e) {
      supplierData = null;
    }
  }

  // Match supplier from database by name
  const searchVendorName = (data.nama_perusahaan || data.nama_supplier || data.nama_sales || '').trim();
  const searchLower = searchVendorName.toLowerCase();

  // 1. Exact match with nama_sales
  let matchedSupplier = dbSuppliers.find(s => {
    if (!s.nama_sales || !searchLower) return false;
    return s.nama_sales.trim().toLowerCase() === searchLower;
  });

  // 2. Exact match in handled_vendors
  if (!matchedSupplier && searchLower) {
    matchedSupplier = dbSuppliers.find(s => {
      if (!Array.isArray(s.handled_vendors)) return false;
      return s.handled_vendors.some(v => String(v || '').trim().toLowerCase() === searchLower);
    });
  }

  // 3. Fallback: match by whole word boundary (avoiding substring bugs like "on parfume" in "expresion parfume")
  if (!matchedSupplier && searchLower) {
    matchedSupplier = dbSuppliers.find(s => {
      if (!s.nama_sales) return false;
      const sName = s.nama_sales.trim().toLowerCase();
      if (!sName) return false;
      const escapedSearch = searchLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const escapedSName = sName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regexSearch = new RegExp('(^|[^a-z0-9])' + escapedSearch + '([^a-z0-9]|$)', 'i');
      const regexSName = new RegExp('(^|[^a-z0-9])' + escapedSName + '([^a-z0-9]|$)', 'i');
      return regexSearch.test(sName) || regexSName.test(searchLower);
    });
  }

  const defaultBuyer = {
    company_name: companySettings?.company_name || 'PT SATALA DERMATECH ESSENTIAL',
    npwp: companySettings?.npwp || '81.383.010.0-404.000',
    address: companySettings?.address || 'Jalan Aster Kavling Paspampres, Desa Kota Batu, Kec. Ciomas, Kab. Bogor, Jawa Barat 16610',
    phone: companySettings?.phone || '(021) 8230 2262',
    email: companySettings?.email || 'info@satala.co.id',
    website: companySettings?.website || 'www.satala.co.id',
    pic: pic || 'Muhammad Fadli'
  };

  const finalBuyer = buyerData ? {
    company_name: buyerData.company_name || defaultBuyer.company_name,
    npwp: buyerData.npwp || defaultBuyer.npwp,
    address: buyerData.address || defaultBuyer.address,
    phone: buyerData.phone || defaultBuyer.phone,
    email: buyerData.email || defaultBuyer.email,
    website: buyerData.website || defaultBuyer.website,
    pic: buyerData.pic || defaultBuyer.pic,
  } : defaultBuyer;

  const vendorName = data.nama_perusahaan || (data.nama_sales && data.nama_sales !== '-' ? data.nama_sales : null) || matchedSupplier?.nama_sales || supplierData?.nama_perusahaan || data.nama_supplier || '-';
  const vendorAddress = data.alamat_supplier || data.supplier_address || matchedSupplier?.alamat || supplierData?.alamat || '-';
  const vendorPhone = data.telepon_supplier || data.supplier_phone || data.phone || matchedSupplier?.phone || matchedSupplier?.telepon || supplierData?.telepon || '-';
  const vendorEmail = data.email_supplier || data.supplier_email || matchedSupplier?.email || supplierData?.email || '-';
  const vendorPic = data.pic_supplier || data.atas_nama || matchedSupplier?.atas_nama || matchedSupplier?.nama_sales || supplierData?.pic || vendorName;
  const defaultSyarat = [
    "Barang harus sesuai dengan spesifikasi dan jumlah yang tertera.",
    "Mohon sertakan COA (Certificate of Analysis) pada setiap pengiriman.",
    "Pengiriman ke gudang utama PT Satala Dermatech Essential.",
    "Mohon konfirmasi penerimaan PO ini."
  ];

  let syaratList = defaultSyarat;
  if (data.syarat_po) {
    try {
      const parsed = typeof data.syarat_po === 'string' ? JSON.parse(data.syarat_po) : data.syarat_po;
      if (Array.isArray(parsed) && parsed.length > 0) {
        syaratList = parsed;
      } else if (typeof data.syarat_po === 'string' && data.syarat_po.trim()) {
        syaratList = data.syarat_po.split('\n').filter(Boolean);
      }
    } catch (e) {
      if (typeof data.syarat_po === 'string' && data.syarat_po.trim()) {
        syaratList = data.syarat_po.split('\n').filter(Boolean);
      }
    }
  }

  let subtotalBarang = 0;
  let ppnTotal = 0;
  let hasPpn = false;
  let mainPpnPct = 11;

  items.forEach(it => {
    const qty = it.qty || 0;
    let basePrice = 0;
    const isPpnExclude = it.ppn_type === 'exclude' || data.ppn_status === 'exclude';
    const isPpnInclude = it.ppn_type === 'include' || data.ppn_status === 'include';
    const ppnPct = parseFloat(it.ppn_percentage) || parseFloat(data.ppn_percentage) || 11;

    if (isPpnExclude) {
      hasPpn = true;
      mainPpnPct = ppnPct;
      if (it.harga_sebelum_ppn && parseFloat(it.harga_sebelum_ppn) > 0) {
        basePrice = parseFloat(it.harga_sebelum_ppn);
      } else {
        basePrice = parseFloat(it.harga_rupiah || 0);
      }
      const finalPrice = parseFloat(it.harga_rupiah || 0);
      if (finalPrice > basePrice) {
        ppnTotal += (finalPrice - basePrice) * qty;
      } else {
        ppnTotal += (basePrice * (ppnPct / 100)) * qty;
      }
    } else if (isPpnInclude && (data.ppn_nominal > 0 || (it.harga_rupiah && parseFloat(it.harga_rupiah) > 0))) {
      const finalPrice = parseFloat(it.harga_rupiah || 0);
      basePrice = finalPrice / (1 + (ppnPct / 100));
      hasPpn = true;
      mainPpnPct = ppnPct;
      ppnTotal += (finalPrice - basePrice) * qty;
    } else {
      basePrice = parseFloat(it.harga_rupiah || 0);
    }
    subtotalBarang += basePrice * qty;
    
    // Save basePrice to item for accurate row display
    it._calculatedBasePrice = basePrice;
  });

  // Override with database exact totals to prevent rounding differences or double calculations
  if (data.total_barang && parseFloat(data.total_barang) > 0) {
    subtotalBarang = parseFloat(data.total_barang);
  }
  if (data.ppn_nominal && parseFloat(data.ppn_nominal) > 0) {
    ppnTotal = parseFloat(data.ppn_nominal);
    hasPpn = true;
  }

  const ongkirVal = ongkir_mode === 'free_ongkir' ? 0 : ongkir_mode === 'transfer' ? (parseFloat(ongkir) || 0) : 0;
  const grandTotal = subtotalBarang + ppnTotal + ongkirVal;

  const formatDisplayPoNum = (rawPo) => {
    if (!rawPo) return 'SDE-PO-AM-110826-001';
    const trimmed = String(rawPo).trim();
    if (/^[A-Z0-9]+-PO-[A-Z0-9]+-\d{6}-\d{3}/i.test(trimmed)) {
      return trimmed.toUpperCase();
    }
    const compAbbr = getCompanyAbbr(finalBuyer?.company_name);
    const suppAbbr = getSupplierAbbr(vendorName);
    const dd = String(validDate.getDate()).padStart(2, '0');
    const mm = String(validDate.getMonth() + 1).padStart(2, '0');
    const yy = String(validDate.getFullYear()).slice(-2);
    const dateTag = `${dd}${mm}${yy}`;
    return `${compAbbr}-PO-${suppAbbr}-${dateTag}-001`;
  };

  const hasUsdItem = items.some(it => it.mata_uang === 'USD' || (it.harga_dollar != null && it.harga_dollar > 0));
  const hasEurItem = items.some(it => it.mata_uang === 'EUR' || (it.harga_euro != null && it.harga_euro > 0));
  const currencySymbol = hasUsdItem ? 'USD' : (hasEurItem ? 'EUR' : 'IDR');
  const displayPoNum = formatDisplayPoNum(po_number);
  const displayPrNum = (data.no_pr || data.pr_number || (po_number ? po_number.replace('PO', 'PR') : 'PR-2025-0361')).replace(/[()]/g, '');

  const explicitPic = data?.requester || data?.nama_requester || data?.pic_name || data?.nama_pic || (data?.pic && data?.pic !== 'Purchasing Staff' ? data?.pic : null) || buyerData?.pic || finalBuyer?.pic || '';
  const normPic = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const cleanTarget = normPic(explicitPic);

  // 1. Matched team member specifically for this PIC
  const matchedBuyerMember = dbTeamMembers.find(m => {
    if (!cleanTarget) return false;
    const fullName = String(m.full_name || m.name || '').toLowerCase().trim();
    const email = String(m.email || '').toLowerCase().trim();
    const cleanFull = normPic(fullName);

    if (cleanFull === cleanTarget) return true;
    if (cleanFull && cleanTarget && (cleanFull.includes(cleanTarget) || cleanTarget.includes(cleanFull))) return true;
    if (cleanTarget.includes('rifai') && cleanFull.includes('rifai')) return true;
    if (cleanTarget.includes('fadli') && cleanFull.includes('fadli')) return true;
    if (email === explicitPic.toLowerCase().trim()) return true;
    return false;
  });

  // 2. Only fallback if the specific matched member has NO signature at all
  const fallbackMember = (!matchedBuyerMember?.signature_url && !matchedBuyerMember?.ttd_url) ? (
    dbTeamMembers.find(m => 
      (m.signature_url || m.ttd_url) &&
      (String(m.role || '').toLowerCase().includes('purchasing') || 
       String(m.division || '').toLowerCase().includes('purchasing') || 
       String(m.jabatan || '').toLowerCase().includes('purchasing') || 
       String(m.divisi || '').toLowerCase().includes('purchasing'))
    ) || sessionMember
  ) : null;

  const buyerSignatureUrl = 
    matchedBuyerMember?.signature_url || 
    matchedBuyerMember?.ttd_url || 
    data?.signature_url || 
    buyerData?.signature_url || 
    fallbackMember?.signature_url || 
    fallbackMember?.ttd_url || 
    companySettings?.signature_url || 
    sessionCompanySettings?.signature_url || 
    null;

  let displayBuyerPic = explicitPic || matchedBuyerMember?.full_name || matchedBuyerMember?.name || finalBuyer?.pic || 'Ahmad Rifai';
  if (displayBuyerPic.trim().toLowerCase() === 'ahmad' || displayBuyerPic.trim().toLowerCase().includes('ahmad')) {
    displayBuyerPic = 'Ahmad Rifai';
  }

  return (
    <div className="hidden print:block font-sans text-[#1E1E1E] p-0 text-[10px] leading-tight w-full">
      <style dangerouslySetInnerHTML={{__html: `
        @media print {
          html, body {
            width: 210mm !important;
            height: auto !important;
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          @page {
            size: A4 portrait;
            margin: 8mm 10mm 8mm 10mm;
          }
          #po-print-area {
            width: 100% !important;
            max-width: 190mm !important;
            margin: 0 auto !important;
            padding: 0 !important;
            box-sizing: border-box !important;
          }
          .avoid-break {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
        }
      `}} />
      
      <div id="po-print-area" className="bg-white p-2">
        
        {/* HEADER SECTION */}
        {(() => {
          const isASB = String(finalBuyer.company_name || '').toLowerCase().includes('apa saja bisa') || 
                        String(finalBuyer.company_name || '').toLowerCase().includes('asb') ||
                        String(po_number || '').toUpperCase().startsWith('ASB-');

          return (
            <div className="flex justify-between items-start pb-3 mb-3 border-b-2 border-[#8C6208]">
              <div className="flex items-center gap-3">
                {!isASB && (
                  <div className="w-12 h-12 bg-white rounded-xl border border-[#E2D8C8] flex items-center justify-center shadow-xs overflow-hidden shrink-0">
                    <img 
                      src={companySettings?.logo_url || '/logo.png'} 
                      alt="Logo" 
                      className="w-10 h-10 object-contain" 
                      onError={(e) => { e.target.src = '/satala-gold-logo.png'; }} 
                    />
                  </div>
                )}
                <div>
                  <h1 className="text-[14px] font-black tracking-tight text-[#302621] uppercase leading-tight">
                    {finalBuyer.company_name}
                  </h1>
                  <p className="text-[7.5px] text-[#666666] max-w-md mt-0.5 leading-tight">
                    {finalBuyer.address}
                  </p>
                </div>
              </div>

              <div className="text-right border border-[#E2D8C8] bg-[#FDFBF7] px-3 py-1.5 rounded-xl shadow-xs shrink-0">
                <span className="text-[8px] font-black text-[#8C6208] uppercase tracking-widest block">PURCHASE ORDER</span>
                <span className="text-[10px] font-black text-[#111111] block tracking-wide">{displayPoNum}</span>
                <span className="text-[7.5px] text-[#777777] block mt-0.5">Tanggal: {dateStr}</span>
              </div>
            </div>
          );
        })()}

        {/* VENDOR & ORDER INFO GRID */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          {/* Card 1: VENDOR / SUPPLIER */}
          <div className="border border-[#E2D8C8] rounded-xl overflow-hidden bg-white shadow-sm flex flex-col">
            <div className="bg-[#F8F4EE] px-3 py-1.5 border-b border-[#E2D8C8] flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5 text-[#8C6208]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5m0 0h4m-4 0V11m0 0h4m-4 0H9m4 0V7m0 0h4m-4 0H9" />
                </svg>
                <span className="text-[9.5px] font-extrabold text-[#302621] uppercase tracking-wider">VENDOR / SUPPLIER</span>
              </div>
              <span className="text-[8px] bg-[#8C6208]/10 text-[#8C6208] px-1.5 py-0.5 rounded font-bold">RESI</span>
            </div>
            <div className="p-2.5 text-[9px] space-y-1 flex-1">
              <div className="flex"><span className="w-24 font-bold text-[#555555] shrink-0">Nama Vendor</span><span className="font-extrabold text-[#111111] flex-1 truncate">: {vendorName}</span></div>
              <div className="flex items-start"><span className="w-24 font-bold text-[#555555] shrink-0">Alamat</span><span className="font-medium text-[#222222] flex-1 leading-tight line-clamp-2">: {vendorAddress}</span></div>
              <div className="flex"><span className="w-24 font-bold text-[#555555] shrink-0">Kontak</span><span className="font-medium text-[#222222] flex-1 truncate">: {vendorPhone}</span></div>
              <div className="flex"><span className="w-24 font-bold text-[#555555] shrink-0">Email</span><span className="font-medium text-[#222222] flex-1 truncate">: {vendorEmail}</span></div>
              <div className="flex"><span className="w-24 font-bold text-[#555555] shrink-0">PIC Vendor</span><span className="font-bold text-[#111111] flex-1 truncate">: {vendorPic}</span></div>
            </div>
          </div>

          {/* Card 2: INFORMASI PEMESANAN */}
          <div className="border border-[#E2D8C8] rounded-xl overflow-hidden bg-white shadow-sm flex flex-col">
            <div className="bg-[#F8F4EE] px-3 py-1.5 border-b border-[#E2D8C8] flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5 text-[#8C6208]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-[9.5px] font-extrabold text-[#302621] uppercase tracking-wider">INFORMASI PEMESANAN</span>
              </div>
              <span className="text-[8px] bg-[#302621]/10 text-[#302621] px-1.5 py-0.5 rounded font-bold">{currencySymbol}</span>
            </div>
            <div className="p-2.5 text-[9px] space-y-1 flex-1">
              <div className="flex"><span className="w-28 font-bold text-[#555555] shrink-0">No. PO</span><span className="font-extrabold text-[#111111] flex-1 truncate">: {displayPoNum}</span></div>
              <div className="flex"><span className="w-28 font-bold text-[#555555] shrink-0">Tanggal PO</span><span className="font-medium text-[#222222] flex-1 truncate">: {dateStr}</span></div>
              <div className="flex"><span className="w-28 font-bold text-[#555555] shrink-0">Departemen</span><span className="font-medium text-[#222222] flex-1 truncate">: Purchasing</span></div>
              <div className="flex"><span className="w-28 font-bold text-[#555555] shrink-0">Requester</span><span className="font-bold text-[#111111] flex-1 truncate">: {displayBuyerPic}</span></div>
              <div className="flex"><span className="w-28 font-bold text-[#555555] shrink-0">Estimasi Kirim</span><span className="font-medium text-[#222222] flex-1 truncate">: {data.estimasi_pengiriman || data.tanggal_estimasi || '-'}</span></div>
              <div className="flex"><span className="w-28 font-bold text-[#555555] shrink-0">Terms Payment</span><span className="font-extrabold text-[#8C6208] flex-1 truncate">: {metode_pembayaran === 'Tempo' ? `${tanggal_jatuh_tempo || '30 Hari'} (Tempo)` : 'Cash / Transfer'}</span></div>
              {((currencySymbol && currencySymbol !== 'IDR' && currencySymbol !== 'Rp') || data.kurs_usd || data.kurs_eur) && (
                <div className="flex bg-amber-50/80 -mx-1 px-1 py-0.5 rounded border border-amber-200/60 mt-0.5">
                  <span className="w-28 font-bold text-amber-900 shrink-0">Kurs Digunakan</span>
                  <span className="font-extrabold text-[#8C6208] flex-1 truncate">: {data.kurs_eur ? `1 EUR = Rp ${fmt(data.kurs_eur)}` : `1 USD = Rp ${fmt(data.kurs_usd || 17738)}`}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ITEMS TABLE */}
        <div className="mb-3 rounded-xl overflow-hidden border border-[#D9CBB7] shadow-sm">
          <table className="w-full text-[9px] border-collapse">
            <thead>
              <tr className="bg-[#302621] text-white border-b border-[#302621]">
                <th className="text-center px-2 py-2 font-black w-8 border-r border-white/20">NO.</th>
                <th className="text-left px-3 py-2 font-black border-r border-white/20">DESKRIPSI BARANG</th>
                <th className="text-left px-3 py-2 font-black border-r border-white/20">SPESIFIKASI</th>
                <th className="text-center px-2 py-2 font-black w-14 border-r border-white/20">QTY</th>
                <th className="text-center px-2 py-2 font-black w-16 border-r border-white/20">SATUAN</th>
                <th className="text-right px-3 py-2 font-black border-r border-white/20">HARGA SATUAN</th>
                <th className="text-right px-3 py-2 font-black">TOTAL</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2D8C8]/70 text-[#222222]">
              {items.map((item, idx) => {
                const qty = item.qty || 0;
                const isForeign = item.mata_uang && item.mata_uang !== 'IDR';
                const foreignPrice = isForeign ? (item.harga_dollar || item.harga_euro) : null;
                const symbol = item.mata_uang === 'USD' ? '$' : (item.mata_uang === 'EUR' ? '€' : 'Rp');
                
                let basePrice = item._calculatedBasePrice || 0;
                const finalPrice = basePrice;
                const rowTotal = finalPrice * qty;

                const specText = item.spesifikasi 
                  || item.kode_item 
                  || item.kode 
                  || item.sku 
                  || item.kemasan 
                  || item.keterangan 
                  || (item.kategori ? `Kategori: ${item.kategori}` : '-');

                return (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#FAF8F4]'}>
                    <td className="px-2 py-2 text-center font-bold text-[#666666] border-r border-[#E2D8C8]">{idx + 1}</td>
                    <td className="px-3 py-2 font-extrabold text-[#111111] border-r border-[#E2D8C8]">
                      <div>{item.name || item.nama || item.nama_item || item.nama_barang}</div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        {item.kategori && <span className="text-[8px] text-[#666666] italic font-normal">{item.kategori}</span>}
                        {item.ppn_type === 'exclude' ? (
                          <span className="text-[7.5px] font-extrabold text-rose-700 bg-rose-50 px-1 py-0.2 rounded border border-rose-200">Exclude PPN</span>
                        ) : (
                          <span className="text-[7.5px] font-extrabold text-emerald-700 bg-emerald-50 px-1 py-0.2 rounded border border-emerald-200">Include PPN</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-[#444444] text-[8.5px] border-r border-[#E2D8C8]">
                      {specText}
                    </td>
                    <td className="px-2 py-2 text-center font-black text-[#111111] border-r border-[#E2D8C8]">{fmtQtyVal(qty)}</td>
                    <td className="px-2 py-2 text-center text-[#555555] font-semibold border-r border-[#E2D8C8]">{item.satuan || 'pcs'}</td>
                    <td className="px-3 py-2 text-right font-medium text-[#333333] border-r border-[#E2D8C8]">
                      {isForeign && foreignPrice ? `${symbol} ${fmt(foreignPrice)}` : `Rp ${fmt(finalPrice)}`}
                    </td>
                    <td className="px-3 py-2 text-right font-black text-[#111111]">
                      Rp {fmt(rowTotal)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* KETERANGAN & TOTAL SUMMARY SECTION */}
        <div className="grid grid-cols-12 gap-3 mb-5 border border-[#D9CBB7] rounded-xl overflow-hidden bg-white shadow-sm">
          {/* Left: KETERANGAN & Terbilang */}
          <div className="col-span-7 p-3 border-r border-[#D9CBB7] flex flex-col justify-between">
            <div>
              <h3 className="text-[9px] font-black text-[#302621] uppercase tracking-wider mb-1 flex items-center gap-1">
                <svg className="w-3 h-3 text-[#8C6208]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                KETERANGAN & SYARAT
              </h3>
              <ol className="text-[8px] text-[#444444] space-y-0.5 list-decimal list-inside leading-snug">
                {syaratList.map((syarat, sIdx) => (
                  <li key={sIdx}>{syarat}</li>
                ))}
              </ol>
              {catatan && (
                <div className="mt-1.5 pt-1.5 border-t border-slate-100 text-[8px] text-[#555555]">
                  <span className="font-bold text-[#302621]">Catatan Khusus:</span> {catatan}
                </div>
              )}
              {((currencySymbol && currencySymbol !== 'IDR' && currencySymbol !== 'Rp') || data.kurs_usd || data.kurs_eur) && (
                <div className="mt-1 pt-1 border-t border-[#E2D8C8] text-[8px] text-[#8C6208] font-bold flex items-center gap-1">
                  <span>* Transaksi Valas dengan Kurs Terkunci: 1 {currencySymbol || 'USD'} = Rp {fmt(data.kurs_eur || data.kurs_usd || 17738)}</span>
                </div>
              )}
            </div>

            {/* Terbilang Box */}
            <div className="mt-2 pt-2 border-t border-[#E2D8C8] bg-[#FAF7F2] p-2 rounded-lg border">
              <span className="text-[8px] font-bold text-[#302621] block mb-0.5">Terbilang:</span>
              <span className="text-[8.5px] font-extrabold text-[#8C6208] italic block leading-tight">
                "{terbilangRupiah(grandTotal)}"
              </span>
            </div>
          </div>

          {/* Right: Calculations */}
          <div className="col-span-5 text-[9px] flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-center px-3 py-1.5 border-b border-[#E2D8C8]">
                <span className="font-bold text-[#555555]">SUBTOTAL</span>
                <span className="font-extrabold text-[#111111]">Rp {fmt(subtotalBarang)}</span>
              </div>
              {hasPpn && (
                <div className="flex justify-between items-center px-3 py-1.5 border-b border-[#E2D8C8]">
                  <span className="font-bold text-[#555555]">PPN {mainPpnPct}%</span>
                  <span className="font-bold text-[#111111]">Rp {fmt(ppnTotal)}</span>
                </div>
              )}
              {ongkirVal > 0 && (
                <div className="flex justify-between items-center px-3 py-1.5 border-b border-[#E2D8C8]">
                  <span className="font-bold text-[#555555]">ONGKIR</span>
                  <span className="font-bold text-[#111111]">Rp {fmt(ongkirVal)}</span>
                </div>
              )}
            </div>

            {/* GRAND TOTAL ROW */}
            <div className="flex justify-between items-center px-3 py-2 bg-[#302621] text-white">
              <span className="font-black text-[9.5px] uppercase tracking-wider">TOTAL KESELURUHAN</span>
              <span className="font-black text-[12px] text-[#D4AF37]">Rp {fmt(grandTotal)}</span>
            </div>
          </div>
        </div>

        {/* SIGNATURE BLOCK (Placed DIRECTLY BELOW content) */}
        <div className="pt-2 avoid-break">
          <div className="grid grid-cols-2 gap-8 text-center text-[9px]">
            {/* Left Signature: Satala Purchasing */}
            <div className="flex flex-col items-center">
              <p className="font-bold text-[#302621] mb-0.5">Dibuat & Diajukan Oleh,</p>
              <p className="text-[8px] text-[#666666] font-semibold mb-1">{finalBuyer.company_name}</p>
              
              {/* Purchasing Digital Signature */}
              <div className="h-[45px] flex items-center justify-center my-0.5 relative w-full max-w-[120px]">
                {buyerSignatureUrl ? (
                  <img 
                    src={buyerSignatureUrl} 
                    alt="Signature" 
                    className="h-[55px] max-w-full object-contain mix-blend-multiply scale-110" 
                    {...(typeof buyerSignatureUrl === 'string' && buyerSignatureUrl.startsWith('http') ? { crossOrigin: 'anonymous' } : {})} 
                  />
                ) : (
                  <div className="h-full w-full"></div>
                )}
              </div>

              <div className="w-44 border-t-2 border-[#302621] pt-1 mt-1">
                <p className="font-black text-[#111111] uppercase tracking-wide text-[9.5px]">{displayBuyerPic}</p>
                <p className="text-[7.5px] text-[#666666]">Purchasing Department</p>
              </div>
            </div>

            {/* Right Signature: Vendor / Supplier Confirmation */}
            <div className="flex flex-col items-center relative">
              <p className="font-bold text-[#302621] mb-0.5">Disetujui Oleh (Supplier),</p>
              <p className="text-[8px] text-[#666666] font-semibold mb-10">{vendorName}</p>
              
              {/* Stamp Placeholder Box */}
              <div className="absolute top-4 left-1/2 -translate-x-1/2 w-20 h-12 border border-dashed border-[#8C6208]/40 rounded-lg flex items-center justify-center opacity-30 rotate-[-5deg] pointer-events-none">
                <span className="text-[7px] font-black text-[#8C6208] text-center uppercase leading-tight">
                  CAP / STEMPEL<br/>SUPPLIER
                </span>
              </div>

              <div className="w-44 border-t-2 border-[#302621] pt-1">
                <p className="font-black text-[#111111] uppercase tracking-wide text-[9.5px]">{vendorPic}</p>
                <p className="text-[7.5px] text-[#666666]">Konfirmasi & Stempel Vendor</p>
              </div>
            </div>
          </div>

          <p className="text-center text-[7px] text-[#888888] mt-3 pt-1.5 border-t border-slate-100 italic">
            Dokumen Purchase Order ini diterbitkan secara elektronik oleh {finalBuyer.company_name} dan sah tanpa membutuhkan tanda tangan basah jika disetujui.
          </p>
        </div>

      </div>
    </div>
  );
}


